import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/button";
import { Switch } from "@/shared/ui/switch";
import { Headphones, Sparkles, Users } from "lucide-react";

type Props = {
  advisorEnabled: boolean;
  kfAccountCount: number;
  canManage: boolean;
  advisorPending: boolean;
  onToggleAdvisor: (next: boolean) => void;
  onManageKf: () => void;
};

/**
 * 启用能力区(统一配置模型:一行同时承载客服 + 军师)。
 *  - AI客服:always-on(行本身 module=customer_service);展示已绑客服账号数 + 管理入口。
 *  - 军师工作台:identityMode 开关(TENANT_CHAIN ↔ MEMBER_CHANNEL)。
 */
export function CapabilitySection({
  advisorEnabled,
  kfAccountCount,
  canManage,
  advisorPending,
  onToggleAdvisor,
  onManageKf,
}: Props) {
  const { t } = useTranslation("wecom");
  return (
    <div className="space-y-2">
      {/* AI客服 */}
      <div className="flex items-center gap-3 rounded-md border px-3 py-2.5">
        <Headphones className="h-4 w-4 text-info shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{t("appList.capAiService")}</div>
          <div className="text-2xs text-muted-foreground">{t("appList.capAiServiceDesc")}</div>
          <div className="text-2xs text-muted-foreground/80 mt-0.5">
            {kfAccountCount > 0
              ? t("appList.capKfBound", { count: kfAccountCount })
              : t("appList.capKfNone")}
          </div>
        </div>
        {canManage && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-2xs shrink-0"
            onClick={onManageKf}
          >
            <Users className="h-3 w-3 mr-1" />
            {t("appList.capManageKf")}
          </Button>
        )}
      </div>

      {/* 军师工作台 */}
      <div className="flex items-center gap-3 rounded-md border px-3 py-2.5">
        <Sparkles className="h-4 w-4 text-chart-2 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{t("appList.capAdvisor")}</div>
          <div className="text-2xs text-muted-foreground">{t("appList.capAdvisorDesc")}</div>
        </div>
        <span className="text-2xs text-muted-foreground shrink-0">
          {advisorEnabled ? t("appList.capAdvisorOn") : t("appList.capAdvisorOff")}
        </span>
        <Switch
          checked={advisorEnabled}
          disabled={!canManage || advisorPending}
          onCheckedChange={(v) => onToggleAdvisor(v)}
          aria-label={t("appList.capAdvisor")}
        />
      </div>
    </div>
  );
}
